import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Hash, Volume2, ChevronDown, Plus, Settings, Mic, Headphones } from "lucide-react";
import {
  DM_CONVERSATIONS,
  GROUP_CHATS,
  SERVERS,
  getCategoriesForServer,
  getChannelsForCategory,
  getUncategorizedChannels,
  getInitials,
  getAvatarColor,
  formatTimestamp,
  PRESENCES,
} from "@/mock/data";
import type { Channel } from "@snowcord/shared";
import { useModalStore } from "@/store/modalStore";
import { useAuthStore } from "@/store/authStore";
import "./Sidebar.css";

function PresenceDot({ userId }: { userId: string }) {
  const status = PRESENCES[userId] || "offline";
  const cls = `sidebar__dm-presence sidebar__dm-presence--${status}`;
  return <div className={cls} />;
}

function DMSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");

  const filteredDMs = DM_CONVERSATIONS.filter((dm) =>
    dm.user.displayName.toLowerCase().includes(search.toLowerCase()),
  );
  const filteredGroups = GROUP_CHATS.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <div className="sidebar__header">
        <span className="sidebar__header-title">Direct Messages</span>
        <button className="sidebar__header-action" aria-label="New DM">
          <Plus size={16} />
        </button>
      </div>

      <div className="sidebar__search">
        <input
          className="sidebar__search-input"
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="sidebar__list">
        <div className="sidebar__section-header">Friends</div>
        {filteredDMs.map((dm) => {
          const isActive = location.pathname === `/app/dm/${dm.userId}`;
          const lastMsg = dm.lastMessage;
          const preview = lastMsg
            ? lastMsg.content.length > 36
              ? lastMsg.content.slice(0, 36) + "..."
              : lastMsg.content
            : "No messages yet";

          return (
            <div
              key={dm.userId}
              className={`sidebar__dm-item${isActive ? " sidebar__dm-item--active" : ""}${dm.isMuted ? " sidebar__dm-item--muted" : ""}`}
              onClick={() => navigate(`/app/dm/${dm.userId}`)}
              role="button"
              tabIndex={0}
            >
              <div className="sidebar__dm-avatar">
                <div
                  className="sidebar__dm-avatar-img"
                  style={{ background: getAvatarColor(dm.userId) }}
                >
                  {getInitials(dm.user.displayName)}
                </div>
                <PresenceDot userId={dm.userId} />
              </div>

              <div className="sidebar__dm-content">
                <div className="sidebar__dm-name">{dm.user.displayName}</div>
                <div className="sidebar__dm-preview">{preview}</div>
              </div>

              <div className="sidebar__dm-meta">
                {lastMsg && (
                  <span className="sidebar__dm-time">
                    {formatTimestamp(lastMsg.createdAt)}
                  </span>
                )}
                {dm.unreadCount > 0 && (
                  <span className="sidebar__dm-badge">{dm.unreadCount}</span>
                )}
              </div>
            </div>
          );
        })}

        <div className="sidebar__section-header">Group Chats</div>
        {filteredGroups.map((group) => {
          const isActive = location.pathname === `/app/group/${group.id}`;
          const lastMsg = group.lastMessage;
          const preview = lastMsg
            ? lastMsg.content.length > 36
              ? lastMsg.content.slice(0, 36) + "..."
              : lastMsg.content
            : "No messages yet";

          return (
            <div
              key={group.id}
              className={`sidebar__group-item${isActive ? " sidebar__group-item--active" : ""}`}
              onClick={() => navigate(`/app/group/${group.id}`)}
              role="button"
              tabIndex={0}
            >
              <div
                className="sidebar__group-avatar"
                style={{ background: getAvatarColor(group.id) }}
              >
                {getInitials(group.name)}
              </div>

              <div className="sidebar__group-content">
                <div className="sidebar__group-name">{group.name}</div>
                <div className="sidebar__group-preview">{preview}</div>
              </div>

              <div className="sidebar__group-meta">
                {lastMsg && (
                  <span className="sidebar__group-time">
                    {formatTimestamp(lastMsg.createdAt)}
                  </span>
                )}
                {group.unreadCount > 0 && (
                  <span className="sidebar__group-badge">
                    {group.unreadCount}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ChannelItem({
  channel,
  serverId,
  isActive,
}: {
  channel: Channel;
  serverId: string;
  isActive: boolean;
}) {
  const navigate = useNavigate();

  return (
    <div
      className={`sidebar__channel-item${isActive ? " sidebar__channel-item--active" : ""}`}
      onClick={() => navigate(`/app/server/${serverId}/channel/${channel.id}`)}
      role="button"
      tabIndex={0}
    >
      <span className="sidebar__channel-icon">
        {channel.type === "voice" ? (
          <Volume2 size={18} />
        ) : (
          <Hash size={18} />
        )}
      </span>
      <span className="sidebar__channel-name">{channel.name}</span>
    </div>
  );
}

function ServerSidebar({ serverId }: { serverId: string }) {
  const location = useLocation();
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set(),
  );
  const server = SERVERS.find((s) => s.id === serverId);
  if (!server) return null;

  const categories = getCategoriesForServer(serverId);
  const uncategorized = getUncategorizedChannels(serverId);

  function toggleCategory(catId: string) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  }

  return (
    <>
      <div className="sidebar__header">
        <span className="sidebar__header-title">{server.name}</span>
        <button className="sidebar__header-action" aria-label="Server settings">
          <Settings size={16} />
        </button>
      </div>

      <div className="sidebar__list">
        {uncategorized.map((ch) => (
          <ChannelItem
            key={ch.id}
            channel={ch}
            serverId={serverId}
            isActive={location.pathname === `/app/server/${serverId}/channel/${ch.id}`}
          />
        ))}

        {categories.map((cat) => {
          const isCollapsed = collapsedCategories.has(cat.id);
          const channels = getChannelsForCategory(cat.id);

          return (
            <div key={cat.id}>
              <div
                className="sidebar__category-header"
                onClick={() => toggleCategory(cat.id)}
                role="button"
                tabIndex={0}
              >
                <span
                  className={`sidebar__category-arrow${isCollapsed ? " sidebar__category-arrow--collapsed" : ""}`}
                >
                  <ChevronDown size={12} />
                </span>
                <span className="sidebar__category-name">{cat.name}</span>
                <span className="sidebar__category-add">
                  <Plus size={12} />
                </span>
              </div>

              {!isCollapsed &&
                channels.map((ch) => (
                  <div key={ch.id}>
                    <ChannelItem
                      channel={ch}
                      serverId={serverId}
                      isActive={
                        location.pathname ===
                        `/app/server/${serverId}/channel/${ch.id}`
                      }
                    />
                    {ch.type === "voice" && (
                      <div className="sidebar__voice-info">
                        <Volume2 size={12} /> 0
                      </div>
                    )}
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

export function Sidebar() {
  const location = useLocation();
  const isServerRoute = location.pathname.startsWith("/app/server/");
  const serverId = isServerRoute ? location.pathname.split("/")[3] : null;
  const openModal = useModalStore((s) => s.openModal);
  const user = useAuthStore((s) => s.user);

  const displayName = user?.displayName || "Unknown";
  const username = user?.username || "unknown";
  const userId = user?.id || "u1";
  const createdAt = user?.createdAt;
  const isAdmin = user?.isAdmin;

  return (
    <aside className="sidebar">
      {serverId ? (
        <ServerSidebar serverId={serverId} />
      ) : (
        <DMSidebar />
      )}

      <div className="sidebar__user-area">
        <div
          className="sidebar__user-avatar"
          style={{ background: getAvatarColor(userId) }}
          onClick={() => openModal("profile", { userId, displayName, username, createdAt, isAdmin })}
          role="button"
          tabIndex={0}
        >
          {getInitials(displayName)}
        </div>
        <div className="sidebar__user-info">
          <div className="sidebar__user-name">{displayName}</div>
          <div className="sidebar__user-status">Online</div>
        </div>
        <div className="sidebar__user-controls">
          <button className="sidebar__user-btn" aria-label="Mute">
            <Mic size={16} />
          </button>
          <button className="sidebar__user-btn" aria-label="Deafen">
            <Headphones size={16} />
          </button>
          <button className="sidebar__user-btn" aria-label="User settings" onClick={() => openModal("settings")}>
            <Settings size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
